import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../contractorApi';
import { storageBaseUrl } from '../../api';

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const docNo = (prefix, id, date) => {
    const d = date ? new Date(date) : new Date();
    return `${prefix}-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}-${String(id).padStart(3, '0')}`;
};
const fmt = (n) => Number(n || 0).toLocaleString();

// ── TrashIcon ──────────────────────────────────────────────────
const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);

// ── インプット共通スタイル ──────────────────────────────────────
const INPUT = {
    width: '100%',
    padding: '10px 16px',
    fontSize: '0.9rem',
    border: '1px solid #e0e4ea',
    borderRadius: 4,
    outline: 'none',
    boxSizing: 'border-box',
    color: '#1a1a2e',
    background: '#fff',
    transition: 'border-color 0.15s',
};
const INPUT_RO = { ...INPUT, background: '#f5f6f8', color: '#6b7280', cursor: 'default' };
const CARD = { background: '#fff', borderRadius: 8, padding: '24px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' };
const H2 = { fontSize: '1.2rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 24px' };
const LBL = { display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#1a1a2e', marginBottom: 8 };
const FIELD = { marginBottom: 16 };

// ── A4プレビュー ────────────────────────────────────────────────
const A4Preview = ({ projectId, estimateDate, expiresAt, subject, customerName, customerPostal, customerAddress, contractor, breakdown, note, subtotal, tax, totalAmount, filledRows, rowAmount }) => {
    const fmtDate = (val) => {
        if (!val) return '';
        try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
        catch { return val; }
    };
    const emptyRowCount = Math.max(0, 5 - breakdown.length);

    return (
        <div style={{ padding: '32px', fontSize: '11px', lineHeight: 1.6, color: '#1a1a2e' }}>
            {/* タイトル */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>見積書</h1>
            </div>

            {/* 顧客情報（左）・見積情報（右） */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, gap: 16 }}>
                <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: 700, margin: '0 0 2px', fontSize: '13px' }}>{customerName || '（未入力）'} 様</p>
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '10px' }}>〒{customerPostal}</p>
                    <p style={{ color: '#6b7280', margin: '0 0 12px', fontSize: '10px' }}>{customerAddress}</p>
                    <p style={{ margin: 0, fontSize: '10px' }}>件名: {subject || '（未入力）'}</p>
                </div>
                <div style={{ textAlign: 'right', fontSize: '10px' }}>
                    <div><span style={{ color: '#6b7280' }}>見積日</span><span style={{ marginLeft: 12 }}>{fmtDate(estimateDate)}</span></div>
                    <div><span style={{ color: '#6b7280' }}>見積番号</span><span style={{ marginLeft: 12 }}>{docNo('Z', projectId, estimateDate)}</span></div>
                    {expiresAt && <div><span style={{ color: '#6b7280' }}>有効期限</span><span style={{ marginLeft: 12 }}>{fmtDate(expiresAt)}</span></div>}
                </div>
            </div>

            {/* 業者情報（右寄せ）+ 印鑑 */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                <div style={{ textAlign: 'right' }}>
                    <p style={{ fontWeight: 700, margin: '0 0 2px', fontSize: '10px' }}>{contractor?.company_name || ''}</p>
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '9px' }}>{contractor?.representative || ''}</p>
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '9px' }}>〒{contractor?.postal_code || ''}</p>
                    <p style={{ color: '#6b7280', margin: '0 0 8px', fontSize: '9px' }}>{contractor?.prefecture || ''}{contractor?.city || ''}{contractor?.address || ''}</p>
                    {contractor?.seal_image ? (
                        <img src={`${storageBaseUrl}/${contractor.seal_image}`} alt="印鑑" style={{ width: 40, height: 40, objectFit: 'contain', opacity: 0.82, mixBlendMode: 'multiply' }} />
                    ) : (
                        <div style={{ display: 'inline-flex', width: 40, height: 40, border: '2px solid #ef4444', borderRadius: '50%', alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontSize: '9px' }}>印</div>
                    )}
                </div>
            </div>

            {/* 金額サマリーボックス */}
            <div style={{ border: '2px solid #1a1a2e', borderRadius: 4, padding: '12px', marginBottom: 24, background: '#f9fafb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '10px' }}>
                        <div style={{ display: 'flex', gap: 24, marginBottom: 2 }}>
                            <span style={{ color: '#6b7280' }}>小計</span>
                            <span style={{ fontWeight: 500 }}>{fmt(subtotal)}円</span>
                        </div>
                        <div style={{ display: 'flex', gap: 24 }}>
                            <span style={{ color: '#6b7280' }}>消費税</span>
                            <span style={{ fontWeight: 500 }}>{fmt(tax)}円</span>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '20px', fontWeight: 700, color: '#1a1a2e' }}>{fmt(totalAmount)}円</div>
                        <div style={{ fontSize: '9px', color: '#6b7280' }}>見積金額</div>
                    </div>
                </div>
            </div>

            {/* 明細テーブル */}
            <div style={{ border: '1px solid #1a1a2e', marginBottom: 16 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #1a1a2e' }}>
                            {[['摘要', 'left', ''], ['数量', 'center', '64px'], ['単位', 'center', '48px'], ['単価（円）', 'right', '80px'], ['金額', 'right', '96px']].map(([h, align, w]) => (
                                <th key={h} style={{ padding: '8px', textAlign: align, background: '#f9fafb', fontWeight: 600, borderRight: h !== '金額' ? '1px solid #1a1a2e' : 'none', width: w || 'auto' }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {breakdown.map((row, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #e0e4ea' }}>
                                <td style={{ padding: '8px', borderRight: '1px solid #e0e4ea' }}>{row.name || '—'}</td>
                                <td style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid #e0e4ea' }}>{row.quantity || '—'}</td>
                                <td style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid #e0e4ea' }}>{row.unit || '—'}</td>
                                <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #e0e4ea' }}>{fmt(row.unit_price)}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{fmt(rowAmount(row))}</td>
                            </tr>
                        ))}
                        {Array.from({ length: emptyRowCount }).map((_, i) => (
                            <tr key={`e${i}`} style={{ borderBottom: '1px solid #e0e4ea' }}>
                                {[...Array(5)].map((_, j) => (
                                    <td key={j} style={{ padding: '8px', borderRight: j < 4 ? '1px solid #e0e4ea' : 'none' }}>&nbsp;</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 消費税行 */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <div style={{ border: '1px solid #e0e4ea', width: 280, fontSize: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px' }}>
                        <span style={{ color: '#6b7280' }}>消費税</span>
                        <span>10%対象{fmt(subtotal)}</span>
                        <span>{fmt(tax)}円</span>
                    </div>
                </div>
            </div>

            {/* 備考 */}
            {note && (
                <div style={{ paddingTop: 12, borderTop: '1px solid #e0e4ea' }}>
                    <p style={{ fontWeight: 700, margin: '0 0 4px', fontSize: '10px' }}>備考</p>
                    <pre style={{ color: '#6b7280', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '9px', margin: 0 }}>{note}</pre>
                </div>
            )}
        </div>
    );
};

// ── メイン ────────────────────────────────────────────────────
const EstimateCreatePage = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const contractorId = localStorage.getItem('contractor_id');

    // プレビューから戻った場合は入力済み内容を復元
    const restored = location.state?._fromPreview ? location.state : null;

    const [project, setProject] = useState(null);
    const [contractor, setContractor] = useState(restored?.contractor || null);
    const [existingEstimate, setExistingEstimate] = useState(null);

    const [estimateDate] = useState(today());
    const [expiresAt, setExpiresAt] = useState(restored?.expiresAt || '');
    const [subject, setSubject] = useState(restored?.subject || '');

    const [customerName, setCustomerName] = useState(restored?.customerName || '');
    const [customerPostal, setCustomerPostal] = useState(restored?.customerPostal || '');
    const [customerAddress, setCustomerAddress] = useState(restored?.customerAddress || '');

    const [breakdown, setBreakdown] = useState(
        restored?.breakdown || [{ name: '', quantity: 1, unit: '式', unit_price: '' }]
    );
    const [note, setNote] = useState(restored?.note || '');

    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`projects/${projectId}`).then(r => {
            const p = r.data;
            setProject(p);
            // プレビューから戻った場合は入力値を上書きしない
            if (!restored) {
                const name = `${p.customer?.last_name || ''} ${p.customer?.first_name || ''}`.trim();
                setCustomerName(name);
                setSubject(name ? `${name} 様 乗り入れ工事` : '');
                setCustomerPostal(p.customer?.postal_code || '');
                setCustomerAddress(`${p.customer?.prefecture || ''}${p.customer?.city || ''}${p.customer?.address || ''}`);
            }
        }).catch(console.error);
        if (contractorId) {
            api.get(`contractors/${contractorId}`).then(r => setContractor(r.data)).catch(console.error);
        }
        // プレビューから戻った場合は差し戻しチェックをスキップ
        if (!restored) {
            api.get(`estimates/by_project/${projectId}`).then(r => {
                const est = r.data;
                if (est.status === 'rejected') {
                    setExistingEstimate(est);
                    if (est.expires_at) setExpiresAt(est.expires_at.slice(0, 10));
                    if (est.breakdown?.length > 0) {
                        setBreakdown(est.breakdown.map(row => ({
                            name: row.name || '',
                            quantity: row.quantity ?? 1,
                            unit: row.unit || '式',
                            unit_price: row.unit_price ?? '',
                        })));
                    }
                    if (est.note) setNote(est.note);
                }
            }).catch(() => {});
        }
    }, [projectId, contractorId]); // eslint-disable-line react-hooks/exhaustive-deps

    const addRow = () => setBreakdown(prev => [...prev, { name: '', quantity: 1, unit: '式', unit_price: '' }]);
    const removeRow = (i) => setBreakdown(prev => prev.filter((_, idx) => idx !== i));
    const setRow = (i, field, val) => {
        setBreakdown(prev => { const next = [...prev]; next[i] = { ...next[i], [field]: val }; return next; });
    };

    const rowAmount = (row) => Math.round((Number(row.quantity) || 0) * (Number(row.unit_price) || 0));
    const subtotal = breakdown.reduce((sum, row) => sum + rowAmount(row), 0);
    const tax = Math.round(subtotal * 0.1);
    const totalAmount = subtotal + tax;
    const filledRows = breakdown.filter(r => r.name);

    const handlePreview = () => {
        if (totalAmount <= 0) { setError('金額を入力してください。'); return; }
        setError(null);
        navigate(`/contractor/estimate/preview/${projectId}`, {
            state: {
                estimateDate, expiresAt, subject, customerName, customerPostal, customerAddress,
                contractor, breakdown, note, subtotal, tax, totalAmount,
                existingEstimateId: existingEstimate?.id ?? restored?.existingEstimateId ?? null,
            },
        });
    };

    const previewProps = { projectId, estimateDate, expiresAt, subject, customerName, customerPostal, customerAddress, contractor, breakdown, note, subtotal, tax, totalAmount, filledRows, rowAmount };

    return (
        <div style={{ minHeight: '100vh', background: '#f5f6f8' }}>
            <style>{`
                .est-inp:focus { border-color: #005c53 !important; }
                .est-main-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; align-items: start; }
                .est-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
                .est-item { display: grid; grid-template-columns: 5fr 2fr 2fr 2fr auto; gap: 12px; padding-bottom: 16px; margin-bottom: 16px; align-items: end; }
                .est-preview-sticky { position: sticky; top: 24px; }
                .est-field-label { display: none !important; }
                .est-items-header { display: grid; grid-template-columns: 5fr 2fr 2fr 2fr auto; gap: 12px; padding-bottom: 8px; margin-bottom: 4px; border-bottom: 1px solid #e0e4ea; }
                @media (max-width: 768px) {
                    .est-main-grid { grid-template-columns: 1fr !important; gap: 0 !important; }
                    .est-2col { grid-template-columns: 1fr !important; }
                    .est-item { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
                    .est-item-name { grid-column: 1 / -1 !important; }
                    .est-item-del { grid-column: 2; grid-row: 2; display: flex; justify-content: flex-end; align-items: flex-end; }
                    .est-preview-sticky { position: static !important; }
                    .est-field-label { display: block !important; }
                    .est-items-header { display: none !important; }
                }
                @media print {
                    @page { size: A4; margin: 20mm; }
                    .no-print { display: none !important; }
                    body { background: #fff; }
                    .a4-inner { font-size: 11px !important; }
                }
            `}</style>

            <div className="no-print" style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 24px' }}>
                {/* ページタイトル */}
                <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 16px' }}>
                    {existingEstimate ? '見積書を修正する' : '見積書を作成する'}
                </h1>

                {/* 差し戻し理由バナー */}
                {existingEstimate && (
                    <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, padding: '12px 16px', marginBottom: 24 }}>
                        <p style={{ fontWeight: 600, fontSize: '0.875rem', color: '#92400e', margin: '0 0 4px' }}>
                            顧客から修正の依頼が届いています
                        </p>
                        {existingEstimate.rejection_reason ? (
                            <p style={{ fontSize: '0.84rem', color: '#78350f', margin: 0, whiteSpace: 'pre-wrap' }}>
                                {existingEstimate.rejection_reason}
                            </p>
                        ) : (
                            <p style={{ fontSize: '0.84rem', color: '#92400e', margin: 0 }}>
                                理由は記載されていません。前回の内容を確認して修正してください。
                            </p>
                        )}
                    </div>
                )}

                {/* 2カラム */}
                <div className="est-main-grid">

                    {/* ── 左：フォーム ───────────────────────────────── */}
                    <div>
                        {/* 見積情報 */}
                        <div style={CARD}>
                            <h2 style={H2}>見積情報</h2>
                            <div style={FIELD}>
                                <label style={LBL}>件名</label>
                                <input className="est-inp" value={subject} onChange={e => setSubject(e.target.value)} placeholder="例：山田 様 乗り入れ工事" style={INPUT} />
                            </div>
                            <div className="est-2col">
                                <div>
                                    <label style={LBL}>見積番号</label>
                                    <input className="est-inp" value={docNo('Z', projectId, estimateDate)} readOnly style={INPUT_RO} />
                                </div>
                                <div>
                                    <label style={LBL}>見積日</label>
                                    <input className="est-inp" type="date" value={estimateDate} readOnly style={INPUT_RO} />
                                </div>
                            </div>
                            <div>
                                <label style={LBL}>有効期限</label>
                                <input className="est-inp" type="date" value={expiresAt} onChange={e => { setExpiresAt(e.target.value); }} style={INPUT} />
                            </div>
                        </div>

                        {/* お客様情報 */}
                        <div style={CARD}>
                            <h2 style={H2}>お客様情報</h2>
                            <div style={FIELD}>
                                <label style={LBL}>宛名</label>
                                <input className="est-inp" value={customerName} onChange={e => { setCustomerName(e.target.value); }} placeholder="例：山田 太郎" style={INPUT} />
                            </div>
                            <div style={FIELD}>
                                <label style={LBL}>郵便番号</label>
                                <input className="est-inp" value={customerPostal} onChange={e => { setCustomerPostal(e.target.value); }} placeholder="4550018" style={INPUT} />
                            </div>
                            <div>
                                <label style={LBL}>住所</label>
                                <input className="est-inp" value={customerAddress} onChange={e => { setCustomerAddress(e.target.value); }} style={INPUT} />
                            </div>
                        </div>

                        {/* 取引先情報（自社） */}
                        <div style={CARD}>
                            <h2 style={H2}>取引先情報（自社）</h2>
                            <div style={FIELD}>
                                <label style={LBL}>会社名</label>
                                <input className="est-inp" value={contractor?.company_name || ''} readOnly style={INPUT_RO} />
                            </div>
                            <div style={FIELD}>
                                <label style={LBL}>担当者</label>
                                <input className="est-inp" value={contractor?.representative || ''} readOnly style={INPUT_RO} />
                            </div>
                            <div style={FIELD}>
                                <label style={LBL}>郵便番号</label>
                                <input className="est-inp" value={contractor?.postal_code || ''} readOnly style={INPUT_RO} />
                            </div>
                            <div>
                                <label style={LBL}>住所</label>
                                <input className="est-inp" value={contractor ? `${contractor.prefecture || ''}${contractor.city || ''}${contractor.address || ''}` : ''} readOnly style={INPUT_RO} />
                            </div>
                        </div>

                        {/* 明細 */}
                        <div style={CARD}>
                            <h2 style={H2}>明細</h2>
                            <div>
                                <div className="est-items-header">
                                    {['品目', '数量', '単位', '単価', ''].map((h, idx) => (
                                        <div key={idx} style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>{h}</div>
                                    ))}
                                </div>
                                {breakdown.map((row, i) => (
                                    <div key={i} className="est-item" style={{ borderBottom: i < breakdown.length - 1 ? '1px solid #e0e4ea' : 'none' }}>
                                        <div className="est-item-name">
                                            <label className="est-field-label" style={{ ...LBL, fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>品目</label>
                                            <input className="est-inp" value={row.name} onChange={e => setRow(i, 'name', e.target.value)} placeholder="例：材料費" style={{ ...INPUT, padding: '8px 12px', fontSize: '0.85rem' }} />
                                        </div>
                                        <div>
                                            <label className="est-field-label" style={{ ...LBL, fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>数量</label>
                                            <input className="est-inp" type="number" min="0" step="0.01" value={row.quantity} onChange={e => setRow(i, 'quantity', e.target.value)} placeholder="例：1" style={{ ...INPUT, padding: '8px 12px', fontSize: '0.85rem' }} />
                                        </div>
                                        <div>
                                            <label className="est-field-label" style={{ ...LBL, fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>単位</label>
                                            <input className="est-inp" value={row.unit} onChange={e => setRow(i, 'unit', e.target.value)} style={{ ...INPUT, padding: '8px 12px', fontSize: '0.85rem' }} />
                                        </div>
                                        <div>
                                            <label className="est-field-label" style={{ ...LBL, fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>単価</label>
                                            <input className="est-inp" type="text" inputMode="numeric"
                                                value={row.unit_price === '' ? '' : Number(row.unit_price).toLocaleString()}
                                                onChange={e => setRow(i, 'unit_price', e.target.value.replace(/[^0-9]/g, ''))}
                                                placeholder="例：50,000"
                                                style={{ ...INPUT, padding: '8px 12px', fontSize: '0.85rem' }} />
                                        </div>
                                        <div className="est-item-del" style={{ paddingBottom: 2 }}>
                                            <button onClick={() => removeRow(i)} type="button" disabled={breakdown.length === 1} style={{ padding: '8px', background: 'none', border: 'none', color: '#ef4444', cursor: breakdown.length === 1 ? 'default' : 'pointer', opacity: breakdown.length === 1 ? 0.3 : 1, borderRadius: 4, display: 'flex', alignItems: 'center' }}>
                                                <TrashIcon />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button onClick={addRow} type="button" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', color: '#005c53', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 }}>
                                <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>＋</span> 行を追加する
                            </button>

                            {/* 税計算サマリー */}
                            <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #e0e4ea' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: 8 }}>
                                    <span style={{ color: '#6b7280' }}>小計</span>
                                    <span style={{ color: '#1a1a2e', fontWeight: 500 }}>¥{fmt(subtotal)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: 8 }}>
                                    <span style={{ color: '#6b7280' }}>消費税（10%）</span>
                                    <span style={{ color: '#1a1a2e', fontWeight: 500 }}>¥{fmt(tax)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem', paddingTop: 8, borderTop: '1px solid #e0e4ea' }}>
                                    <span style={{ color: '#1a1a2e', fontWeight: 700 }}>合計金額</span>
                                    <span style={{ color: '#005c53', fontWeight: 700 }}>¥{fmt(totalAmount)}</span>
                                </div>
                            </div>
                        </div>

                        {/* 備考 */}
                        <div style={CARD}>
                            <h2 style={H2}>備考</h2>
                            <textarea
                                className="est-inp"
                                rows={5}
                                placeholder="条件・注意事項など"
                                value={note}
                                onChange={e => { setNote(e.target.value); }}
                                style={{ ...INPUT, resize: 'none', minHeight: 100 }}
                            />
                        </div>
                    </div>

                    {/* ── 右：スティッキープレビュー ────────────────── */}
                    <div className="est-preview-sticky">
                        <div style={{ background: '#fff', borderRadius: 8, padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                            <h2 style={{ ...H2, marginBottom: 16 }}>プレビュー（A4サイズ）</h2>
                            <div style={{ border: '1px solid #e0e4ea', borderRadius: 8, background: '#fff', width: '100%', maxWidth: '210mm', aspectRatio: '210 / 297', margin: '0 auto', overflowY: 'auto' }}>
                                <div className="a4-inner">
                                    <A4Preview {...previewProps} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 送信ボタン */}
                {error && <p style={{ color: '#ef4444', fontSize: '0.88rem', margin: '16px 0 0', textAlign: 'center' }}>{error}</p>}
                <div style={{ marginTop: 32, display: 'flex', justifyContent: 'center' }}>
                    <button
                        type="button"
                        onClick={handlePreview}
                        style={{ width: '100%', maxWidth: 448, padding: '16px 32px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '1.05rem', fontWeight: 500, cursor: 'pointer', transition: 'background 0.2s' }}
                    >
                        プレビューを確認する
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EstimateCreatePage;
